import { useMemo, useState, useSyncExternalStore, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { ProgressBar, TableSkeleton } from './loaders';

const NARROW_QUERY = '(max-width: 639.98px)';

function subscribeNarrow(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const list = window.matchMedia(NARROW_QUERY);
  list.addEventListener('change', onChange);
  return () => list.removeEventListener('change', onChange);
}

function readNarrow(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.(NARROW_QUERY).matches);
}

function useNarrowScreen(): boolean {
  return useSyncExternalStore(subscribeNarrow, readNarrow, () => false);
}

const NESTED_CONTROL =
  'a, button, input, select, textarea, label, summary, [role="button"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="dialog"], [data-overlay], [contenteditable="true"]';

function fromNestedControl(target: EventTarget, row: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  if (!row.contains(target)) return true;
  const control = target.closest(NESTED_CONTROL);
  return Boolean(control && control !== row && row.contains(control));
}

const ROW_FOCUS =
  'cursor-pointer outline-none focus-visible:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';

export interface DataTableColumn<T> {
  id?: string;
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
  numeric?: boolean;
  hideBelow?: 'sm' | 'md' | 'lg';
  sort?: (row: T) => string | number | boolean | null | undefined;
}

export interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function columnSortKey<T>(column: DataTableColumn<T>): string {
  return column.id ?? column.header;
}

function columnRenderKey<T>(column: DataTableColumn<T>, index: number): string {
  return column.id ?? `${index}:${column.header}`;
}

function findSortColumn<T>(columns: DataTableColumn<T>[], key: string): DataTableColumn<T> | undefined {
  return columns.find((candidate) => columnSortKey(candidate) === key) ?? columns.find((candidate) => candidate.header === key);
}

export function sortRows<T>(rows: T[], columns: DataTableColumn<T>[], sort: SortState | null): T[] {
  if (!sort) return rows;
  const column = findSortColumn(columns, sort.column);
  if (!column?.sort) return rows;
  const pick = column.sort;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, key: pick(row) }))
    .sort((a, b) => compareValues(a.key, b.key) * sign || a.index - b.index)
    .map((entry) => entry.row);
}

const HIDE_BELOW: Record<NonNullable<DataTableColumn<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

function alignClass<T>(column: DataTableColumn<T>): string | undefined {
  if (column.align === 'right' || column.numeric) return 'text-end';
  if (column.align === 'center') return 'text-center';
  return undefined;
}

export function DataTable<T>({
  rows,
  columns,
  loading,
  error,
  empty,
  onRowClick,
  rowKey,
  dense,
  rowActions,
  footer,
  stickyHeader,
  rowClassName,
  refreshing,
  defaultSort,
  mobileCards = true,
  partial,
}: {
  rows: T[] | undefined;
  columns: DataTableColumn<T>[];
  loading?: boolean;
  error?: unknown;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  dense?: boolean;
  rowActions?: (row: T) => ReactNode;
  footer?: ReactNode;
  stickyHeader?: boolean;
  rowClassName?: (row: T) => string | undefined;
  refreshing?: boolean;
  defaultSort?: SortState;
  mobileCards?: boolean;
  partial?: boolean;
}) {
  const common = useTranslation('common');
  const narrow = useNarrowScreen();
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);
  const sorted = useMemo(() => (rows ? sortRows(rows, columns, sort) : rows), [rows, columns, sort]);
  const sortedColumn = sort ? findSortColumn(columns, sort.column) : undefined;
  const toggleSort = (column: DataTableColumn<T>) => {
    if (!column.sort) return;
    const key = columnSortKey(column);
    setSort((current) => {
      if (!current || findSortColumn(columns, current.column) !== column) return { column: key, direction: 'asc' };
      if (current.direction === 'asc') return { column: key, direction: 'desc' };
      return null;
    });
  };
  const rowInteraction = (row: T) =>
    onRowClick
      ? {
          tabIndex: 0,
          onClick: (event: MouseEvent<HTMLElement>) => {
            if (fromNestedControl(event.target, event.currentTarget)) return;
            onRowClick(row);
          },
          onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            onRowClick(row);
          },
        }
      : {};
  if (loading) {
    return <TableSkeleton columns={columns.length + (rowActions ? 1 : 0)} dense={dense} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center rounded-xl border border-destructive/30 bg-destructive/5 p-10 text-center text-sm animate-in fade-in-0 duration-200">
        <span className="mb-3 grid h-10 w-10 place-items-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-5 w-5" aria-hidden />
        </span>
        <p className="font-medium text-destructive">{common.t('table.load_failed')}</p>
        <p className="mt-1 text-muted-foreground">{describeError(error)}</p>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground animate-in fade-in-0 duration-200">
        <span className="mb-3 grid h-10 w-10 place-items-center rounded-full bg-muted text-muted-foreground/80">
          <Inbox className="h-5 w-5" aria-hidden />
        </span>
        {empty ?? common.t('table.empty')}
      </div>
    );
  }

  const cellPadding = dense ? 'px-3 py-1.5' : 'px-3 py-2.5';

  if (mobileCards && narrow) {
    const labelled = columns.filter((column) => column.header);
    const unlabelled = columns.filter((column) => !column.header);
    const [lead, ...details] = labelled;
    return (
      <div className="relative overflow-hidden rounded-xl border bg-card shadow-sm">
        <ProgressBar active={Boolean(refreshing)} className="absolute inset-x-0 top-0 z-20" />
        <ul className="divide-y divide-border/70">
          {(sorted ?? rows).map((row) => (
            <li
              key={rowKey(row)}
              {...rowInteraction(row)}
              className={cn(
                'flex flex-col gap-1.5 px-3 transition-colors duration-150',
                dense ? 'py-2' : 'py-3',
                onRowClick ? cn(ROW_FOCUS, 'hover:bg-primary/5') : undefined,
                rowClassName?.(row),
              )}
            >
              {lead && (
                <div className={cn('min-w-0 font-medium text-foreground', (lead.align === 'right' || lead.numeric) && 'tabular')}>
                  <span className="sr-only">{lead.header}: </span>
                  {lead.cell(row)}
                </div>
              )}
              {details.length > 0 && (
                <dl className="grid gap-1 text-sm">
                  {details.map((column, index) => (
                    <div key={columnRenderKey(column, index)} className="flex items-baseline justify-between gap-3">
                      <dt className="shrink-0 text-xs text-muted-foreground">{column.header}</dt>
                      <dd className={cn('min-w-0 text-end', (column.align === 'right' || column.numeric) && 'tabular')}>{column.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {(unlabelled.length > 0 || rowActions) && (
                <div className="flex flex-wrap items-center justify-end gap-1 pt-1">
                  {unlabelled.map((column, index) => (
                    <div key={columnRenderKey(column, index)}>{column.cell(row)}</div>
                  ))}
                  {rowActions?.(row)}
                </div>
              )}
            </li>
          ))}
        </ul>
        {footer && <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/30 px-3 py-2 text-sm text-muted-foreground">{footer}</div>}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl border bg-card shadow-sm">
      <ProgressBar active={Boolean(refreshing)} className="absolute inset-x-0 top-0 z-20" />
      <div className={cn('overflow-x-auto', stickyHeader && 'max-h-[70vh] overflow-y-auto')}>
        <table className="w-full border-collapse text-sm">
          <thead className={cn(stickyHeader && 'sticky top-0 z-10')}>
            <tr className="border-b bg-muted/60">
              {columns.map((column, index) => {
                const active = sort && sortedColumn === column ? sort.direction : null;
                return (
                  <th
                    key={columnRenderKey(column, index)}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={active ? (active === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      'whitespace-nowrap px-3 py-2.5 text-start text-xs font-semibold text-muted-foreground',
                      alignClass(column),
                      column.hideBelow && HIDE_BELOW[column.hideBelow],
                      column.sort && 'p-0',
                    )}
                  >
                    {column.sort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column)}
                        className={cn(
                          'group flex w-full items-center gap-1 px-3 py-2.5 transition-colors hover:text-foreground focus-ring',
                          (column.align === 'right' || column.numeric) && 'flex-row-reverse',
                          column.align === 'center' && 'justify-center',
                          active && 'text-foreground',
                        )}
                      >
                        <span>{column.header}</span>
                        {active === 'asc' ? (
                          <ArrowUp className="h-3.5 w-3.5 text-primary" aria-hidden />
                        ) : active === 'desc' ? (
                          <ArrowDown className="h-3.5 w-3.5 text-primary" aria-hidden />
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />
                        )}
                        {active && partial && (
                          <span className="text-[10px] font-normal text-muted-foreground">{common.t('table.sorted_within_loaded')}</span>
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {rowActions && <th scope="col" className="w-px px-3 py-2.5" aria-label={common.t('table.actions')} />}
            </tr>
          </thead>
          <tbody>
            {(sorted ?? rows).map((row) => (
              <tr
                key={rowKey(row)}
                {...rowInteraction(row)}
                className={cn(
                  'border-b border-border/70 transition-colors duration-150 last:border-0 odd:bg-muted/20',
                  onRowClick ? cn(ROW_FOCUS, 'hover:bg-primary/5') : 'hover:bg-muted/40',
                  rowClassName?.(row),
                )}
              >
                {columns.map((column, index) => (
                  <td
                    key={columnRenderKey(column, index)}
                    className={cn(
                      cellPadding,
                      index === 0 && 'font-medium text-foreground',
                      (column.align === 'right' || column.numeric) && 'text-end tabular',
                      column.align === 'center' && 'text-center',
                      column.hideBelow && HIDE_BELOW[column.hideBelow],
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
                {rowActions && (
                  <td className={cn(cellPadding, 'text-end')} onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">{rowActions(row)}</div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer && <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/30 px-3 py-2 text-sm text-muted-foreground">{footer}</div>}
    </div>
  );
}
